import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Button,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { styled, useTheme } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SchoolIcon from '@mui/icons-material/School';
import TimerIcon from '@mui/icons-material/Timer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BoltIcon from '@mui/icons-material/Bolt';
import PsychologyAltIcon from '@mui/icons-material/PsychologyAlt';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { motion } from 'framer-motion';
import { SourceFilter, StudyStats, StudyTechnique, UserProgress } from '../types';
import { SpacedRepetition } from '../utils/spacedRepetition';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const DashboardContainer = styled(Box)(({ theme }) => ({
  padding: '20px 16px 48px',
  maxWidth: '1200px',
  margin: '0 auto',
  [theme.breakpoints.up('md')]: {
    padding: '36px 24px 60px'
  }
}));

const MotionCard = motion(Card);

const StatCard = styled(Card)({
  height: '100%',
  overflow: 'hidden',
  position: 'relative',
});

const ProgressSection = styled(Box)({
  marginTop: '32px',
});

const riseIn = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 }
};

interface DashboardProps {
  studyStats: StudyStats;
  userProgress: UserProgress[];
  sourceFilter: SourceFilter;
  onSourceFilterChange: (value: SourceFilter) => void;
  onStartStudy: (technique: StudyTechnique) => void;
}

export const Dashboard: React.FC<DashboardProps> = ({
  studyStats,
  userProgress,
  sourceFilter,
  onSourceFilterChange,
  onStartStudy
}) => {
  const theme = useTheme();
  const [weeklyData, setWeeklyData] = useState<number[]>([]);
  const [techniqueData, setTechniqueData] = useState<any>(null);

  const handleSourceChange = (_event: React.MouseEvent<HTMLElement>, value: SourceFilter | null) => {
    if (value) {
      onSourceFilterChange(value);
    }
  };

  useEffect(() => {
    const weeklyProgress = [12, 15, 8, 22, 18, 25, 20];
    setWeeklyData(weeklyProgress);

    const techniqueChartData = {
      labels: Object.keys(studyStats.techniqueUsage).map((key) =>
        key.replace('_', ' ')
      ),
      datasets: [{
        data: Object.values(studyStats.techniqueUsage),
        backgroundColor: [
          '#0b5fff',
          '#ff7a18',
          '#14b86a',
          '#ffb020',
          '#0e7490'
        ],
        hoverBackgroundColor: [
          '#0b5fff',
          '#ff7a18',
          '#14b86a',
          '#ffb020',
          '#0e7490'
        ]
      }]
    };
    setTechniqueData(techniqueChartData);
  }, [studyStats]);

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          color: theme.palette.text.secondary
        }
      },
    },
    scales: {
      x: {
        ticks: { color: theme.palette.text.secondary },
        grid: { display: false }
      },
      y: {
        ticks: { color: theme.palette.text.secondary },
        grid: { color: 'rgba(15, 23, 42, 0.08)' }
      }
    }
  };

  const weeklyChartData = {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [{
      label: 'Cards Studied',
      data: weeklyData,
      borderColor: theme.palette.primary.main,
      backgroundColor: 'rgba(11, 95, 255, 0.18)',
      tension: 0.35,
      fill: true,
    }],
  };

  const topicProgressData = {
    labels: Object.keys(studyStats.progressByTopic),
    datasets: [{
      label: 'Cards Mastered',
      data: Object.values(studyStats.progressByTopic),
      backgroundColor: 'rgba(255, 122, 24, 0.35)',
      borderColor: theme.palette.secondary.main,
      borderWidth: 1,
    }],
  };

  const dueCards = SpacedRepetition.getDueCards(userProgress);
  const recommendations = SpacedRepetition.getStudyRecommendations(userProgress);

  return (
    <DashboardContainer>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" sx={{ fontWeight: 600 }} gutterBottom>
          Study Command Center
        </Typography>
        <Typography variant="body1" color="text.secondary">
          A high-velocity cockpit for FRCS mastery. Track your momentum, then drop straight into a focused session.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Source filter
          </Typography>
          <ToggleButtonGroup
            value={sourceFilter}
            exclusive
            onChange={handleSourceChange}
            size="small"
            sx={{
              background: 'rgba(255, 255, 255, 0.7)',
              borderRadius: 999,
              p: 0.5,
              '& .MuiToggleButton-root': {
                border: 'none',
                borderRadius: 999,
                px: 2,
                textTransform: 'none',
                fontWeight: 600,
                color: theme.palette.text.secondary
              },
              '& .MuiToggleButton-root.Mui-selected': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.common.white,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark
                }
              }
            }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="frcs">FRCS</ToggleButton>
            <ToggleButton value="journal">Journals</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1.2fr 0.8fr' },
          gap: 3,
          mb: 4
        }}
      >
        <MotionCard
          {...riseIn}
          sx={{
            p: 3,
            background: 'linear-gradient(135deg, rgba(11, 95, 255, 0.16), rgba(255, 122, 24, 0.16))',
            border: '1px solid rgba(11, 95, 255, 0.2)',
          }}
        >
          <Typography variant="overline" color="text.secondary">
            FRCSORT
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Precision recall. No wasted reps.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Tackle due cards, surface weak zones, and keep your streak alive with data-driven study flow.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <Button
              variant="contained"
              onClick={() => onStartStudy(StudyTechnique.ACTIVE_RECALL)}
              startIcon={<BoltIcon />}
              disabled={dueCards.length === 0 && recommendations.newCards === 0}
            >
              Start Active Recall
            </Button>
            <Button
              variant="outlined"
              onClick={() => onStartStudy(StudyTechnique.FEYNMAN)}
              startIcon={<PsychologyAltIcon />}
            >
              Feynman Sprint
            </Button>
            <Button
              variant="outlined"
              onClick={() => onStartStudy(StudyTechnique.INTERLEAVING)}
              startIcon={<LocalFireDepartmentIcon />}
            >
              Interleaving Mix
            </Button>
          </Box>
        </MotionCard>

        <MotionCard {...riseIn} transition={{ duration: 0.45, delay: 0.05 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Today&apos;s Focus
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Fast lane to your next review targets.
            </Typography>
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  {dueCards.length}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1">Cards due now</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Immediate review queue
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                  {recommendations.newCards}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1">New cards</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Recommended to unlock today
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  {recommendations.difficultCards}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1">Hard recalls</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Requires extra attention
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </MotionCard>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2.5, mb: 4 }}>
        <motion.div {...riseIn}>
          <StatCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <SchoolIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" color="primary">
                    {studyStats.totalCards}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Cards
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StatCard>
        </motion.div>

        <motion.div {...riseIn} transition={{ delay: 0.05 }}>
          <StatCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <CheckCircleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" color="success.main">
                    {studyStats.cardsStudied}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cards Studied
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StatCard>
        </motion.div>

        <motion.div {...riseIn} transition={{ delay: 0.1 }}>
          <StatCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <TrendingUpIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" color="warning.main">
                    {studyStats.averageAccuracy.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Accuracy
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StatCard>
        </motion.div>

        <motion.div {...riseIn} transition={{ delay: 0.15 }}>
          <StatCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <TimerIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" color="info.main">
                    {studyStats.totalStudyTime}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Hours Studied
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StatCard>
        </motion.div>
      </Box>

      <ProgressSection>
        <Typography variant="h5" gutterBottom>
          Progress Pulse
        </Typography>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Overall Progress: {studyStats.totalCards > 0
              ? ((studyStats.cardsStudied / studyStats.totalCards) * 100).toFixed(1)
              : 0}%
          </Typography>
          <LinearProgress
            variant="determinate"
            value={studyStats.totalCards > 0
              ? (studyStats.cardsStudied / studyStats.totalCards) * 100
              : 0}
          />
        </Box>
      </ProgressSection>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.6fr 1fr' }, gap: 3, mb: 4 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Weekly Study Activity
            </Typography>
            <Box sx={{ height: 300 }}>
              <Line data={weeklyChartData} options={chartOptions} />
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Technique Mix
            </Typography>
            <Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
              {techniqueData && <Doughnut data={techniqueData} />}
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Progress by Topic
          </Typography>
          <Box sx={{ height: 300 }}>
            <Bar data={topicProgressData} options={chartOptions} />
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Today&apos;s Study Plan
            </Typography>
            <List>
              <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'error.main' }}>
                    {dueCards.length}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="Review Cards Due"
                  secondary={`${dueCards.length} cards need review today`}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'success.main' }}>
                    {recommendations.newCards}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="New Cards"
                  secondary={`${recommendations.newCards} new cards recommended`}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'warning.main' }}>
                    {recommendations.difficultCards}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="Difficult Cards"
                  secondary={`${recommendations.difficultCards} cards need extra attention`}
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Quick Start Sessions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                onClick={() => onStartStudy(StudyTechnique.ACTIVE_RECALL)}
                disabled={dueCards.length === 0 && recommendations.newCards === 0}
              >
                Start Active Recall Session
              </Button>
              <Button
                variant="outlined"
                onClick={() => onStartStudy(StudyTechnique.FEYNMAN)}
              >
                Practice Feynman Technique
              </Button>
              <Button
                variant="outlined"
                onClick={() => onStartStudy(StudyTechnique.INTERLEAVING)}
              >
                Interleaved Practice
              </Button>
              <Button
                variant="outlined"
                onClick={() => onStartStudy(StudyTechnique.DUAL_CODING)}
              >
                Dual Coding Session
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </DashboardContainer>
  );
};
